// src/utils/eliminaCommessa.js
// ═══════════════════════════════════════════════════════════════════════════
// ELIMINARE DEFINITIVAMENTE UNA COMMESSA (ottobre 2026, W422)
// ═══════════════════════════════════════════════════════════════════════════
// Deciso con Paolo: archiviare c'è già; chi sceglie «Elimina definitivamente» (una prova,
// una commessa aperta per errore) vuole che sparisca TUTTO quello che le appartiene,
// avvisato prima di cosa si perde. Prima si cancellavano solo sezioni, attività, campi,
// varianti e scheda sicurezza: budget, costi, rapportini, DDT, SAL, giornale… restavano nei
// registri senza commessa. E l'eliminazione non lasciava traccia nel Log Modifiche.
//
//  • contaCollegati(): quanti documenti di ogni tipo verranno eliminati, e quali sono
//    numerati o con valore fiscale (DDT emessi, rapportini firmati, fatture richieste,
//    ordini ai fornitori, SAL), da mostrare in rosso prima della conferma;
//  • eliminaCommessaCompleta(): cancella tutto (prima i collegati, per ultima la commessa),
//    raccoglie quello che le Regole non permettono di cancellare (es. le ore del diario di
//    un collega), registra l'eliminazione nel Log Modifiche e negli eventi, poi rilegge dal
//    server per confermare che la commessa non c'è più.
// Restano, scollegati: richieste commerciali e offerte (sono la storia commerciale del
// cliente, nate prima della commessa) e il Log Modifiche/eventi (la storia di chi ha fatto cosa).
// ═══════════════════════════════════════════════════════════════════════════
import { collection, query, where, getDocs, getDoc, getDocFromServer, doc, writeBatch, deleteDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { logModifica } from "./logModifica";
import { logEvento } from "./logEvento";

// [raccolta, campo commessa, campo azienda, etichetta singolare/plurale, documento numerato?]
export const RACCOLTE_COMMESSA = [
  ["rapportini", "projectId", "orgId", ["rapportino", "rapportini"], (d) => !!(d.firmaCliente || d.stato === "firmato" || d.numero)],
  ["ddt", "projectId", "orgId", ["DDT", "DDT"], (d) => d.stato === "emesso" || !!d.numeroProgressivo],
  ["ddtIngresso", "projectId", "orgId", ["DDT in ingresso", "DDT in ingresso"], () => false],
  ["richiesteFatturazione", "projectId", "organizationId", ["richiesta di fatturazione", "richieste di fatturazione"], () => true],
  ["ordiniAcquisto", "commessaId", "organizationId", ["ordine al fornitore", "ordini ai fornitori"], (d) => !!d.numero || !!d.numeroOda],
  ["computiSAL", "projectId", "orgId", ["computo / SAL", "computi / SAL"], (d) => (d.periodi || []).length > 0],
  ["sopralluoghi", "projectId", "orgId", ["sopralluogo", "sopralluoghi"], (d) => !!d.numero],
  ["verbali", "projectId", "orgId", ["verbale", "verbali"], (d) => !!d.numero],
  ["comunicazioni", "projectId", "orgId", ["comunicazione", "comunicazioni"], (d) => !!d.numero],
  ["budgetCommessa", "projectId", "organizationId", ["budget", "budget"], () => false],
  ["movimentiEconomici", "projectId", "organizationId", ["voce di costo/ricavo", "voci di costo/ricavo"], () => false],
  ["giornaleCantiereDays", "projectId", "orgId", ["giornata del giornale di cantiere", "giornate del giornale di cantiere"], () => false],
  ["cronoprogrammaFasi", "projectId", "orgId", ["fase del cronoprogramma", "fasi del cronoprogramma"], () => false],
  ["noleggi", "projectId", "orgId", ["noleggio", "noleggi"], () => false],
  ["nuoveForniture", "commessaId", "organizationId", ["richiesta NF", "richieste NF"], () => false],
  ["richiesteMateriali", "projectId", "organizationId", ["richiesta al magazzino", "richieste al magazzino"], () => false],
  ["rientriMateriali", "projectId", "organizationId", ["rientro materiali", "rientri materiali"], () => false],
  ["pratiche", "projectId", "organizationId", ["pratica", "pratiche"], () => false],
  ["documentiPratiche", "projectId", "organizationId", ["documento di pratica", "documenti di pratica"], () => false],
  ["segnalazioniCliente", "projectId", "orgId", ["segnalazione cliente", "segnalazioni cliente"], () => false],
  ["controlliPM", "projectId", "orgId", ["controllo PM", "controlli PM"], () => false],
  ["reportCommessa", "projectId", "orgId", ["report di commessa", "report di commessa"], () => false],
  ["eventiCommessa", "projectId", "organizationId", ["evento in calendario", "eventi in calendario"], () => false],
  ["regole", "projectId", "orgId", ["regola", "regole"], () => false],
  ["contrattiManutenzione", "projectId", "orgId", ["contratto di manutenzione", "contratti di manutenzione"], () => false],
  ["manutenzionePiani", "projectId", "organizationId", ["piano di manutenzione", "piani di manutenzione"], () => false],
  ["manutenzioneInterventi", "projectId", "organizationId", ["intervento di manutenzione", "interventi di manutenzione"], () => false],
  ["manutenzioneImpianti", "projectId", "organizationId", ["impianto in manutenzione", "impianti in manutenzione"], () => false],
  ["attivitaExtra", "projectId", "orgId", ["variante", "varianti"], () => false],
  ["diarioOre", "projectId", "orgId", ["blocco di ore del diario", "blocchi di ore del diario"], () => false],
];
// Documenti con lo stesso id della commessa
const PER_ID = ["sicurezzaCantiere", "giornaleCantiere", "projectSettings"];

const etichetta = (lab, n) => `${n} ${n === 1 ? lab[0] : lab[1]}`;

async function leggiCollegati(project) {
  const org = project.organizationId;
  const esiti = await Promise.all(RACCOLTE_COMMESSA.map(async ([col, campo, campoOrg, lab, numerato]) => {
    try {
      const s = await getDocs(query(collection(db, col), where(campo, "==", project.id), where(campoOrg, "==", org)));
      return { col, lab, docs: s.docs, refs: s.docs.map(d => doc(db, col, d.id)), numerati: s.docs.filter(d => numerato(d.data())).length };
    } catch { return { col, lab, docs: [], refs: [], numerati: 0, illeggibile: true }; }
  }));
  return esiti;
}

/**
 * Riepilogo di cosa verrà eliminato con la commessa.
 * @returns {{ voci: { col, testo, n, numerati }[], numerati: string[] }}
 */
export async function contaCollegati(project) {
  const esiti = await leggiCollegati(project);
  const voci = esiti.filter(e => e.docs.length).map(e => ({ col: e.col, testo: etichetta(e.lab, e.docs.length), n: e.docs.length, numerati: e.numerati }));
  const numerati = esiti.filter(e => e.numerati).map(e => etichetta(e.lab, e.numerati));
  return { voci, numerati };
}

/** Cancella un elenco di riferimenti: a gruppi; se un gruppo è rifiutato, uno per uno. Ritorna quanti NON si sono potuti cancellare. */
async function cancella(refs) {
  let falliti = 0;
  for (let i = 0; i < refs.length; i += 400) {
    const pezzo = refs.slice(i, i + 400);
    try {
      const b = writeBatch(db);
      pezzo.forEach(r => b.delete(r));
      await b.commit();
    } catch {
      for (const r of pezzo) { try { await deleteDoc(r); } catch { falliti++; } }
    }
  }
  return falliti;
}

/**
 * Elimina la commessa e tutto ciò che le appartiene.
 * @param project - documento della commessa (con id e organizationId)
 * @param autore - { uid, nome }
 * @returns {{ ok: boolean, nonEliminati: string[], ancoraPresente: boolean }}
 */
export async function eliminaCommessaCompleta(project, autore) {
  const org = project.organizationId;
  const nonEliminati = [];

  // 1. struttura: sezioni, attività, sottoattività, campi dati
  const sezioni = (await getDocs(query(collection(db, "sections"), where("projectId", "==", project.id)))).docs;
  const attivita = (await getDocs(query(collection(db, "tasks"), where("projectId", "==", project.id), where("organizationId", "==", org)))).docs;
  const sotto = [], campi = [];
  for (let i = 0; i < attivita.length; i += 30) {
    const ids = attivita.slice(i, i + 30).map(d => d.id);
    sotto.push(...(await getDocs(query(collection(db, "subtasks"), where("parentTaskId", "in", ids), where("organizationId", "==", org)))).docs);
  }
  for (let i = 0; i < sezioni.length; i += 30) {
    const ids = sezioni.slice(i, i + 30).map(d => d.id);
    campi.push(...(await getDocs(query(collection(db, "customFields"), where("sectionId", "in", ids)))).docs);
  }

  // 2. struttura e commessa insieme, come prima (le Regole dei campi dati guardano la
  //    commessa, che quindi deve esistere ancora): se questo non riesce, non si tocca nulla
  const struttura = [...sotto.map(d => doc(db, "subtasks", d.id)), ...attivita.map(d => doc(db, "tasks", d.id)),
    ...campi.map(d => doc(db, "customFields", d.id)), ...sezioni.map(d => doc(db, "sections", d.id))];
  const blocchi = [];
  for (let i = 0; i < struttura.length; i += 400) blocchi.push(struttura.slice(i, i + 400));
  if (!blocchi.length || blocchi[blocchi.length - 1].length >= 400) blocchi.push([]);
  blocchi[blocchi.length - 1].push(doc(db, "projects", project.id));
  for (const pezzo of blocchi) { const b = writeBatch(db); pezzo.forEach(r => b.delete(r)); await b.commit(); }

  // 3. tutto il resto collegato alla commessa
  const esiti = await leggiCollegati(project);
  for (const e of esiti) {
    const falliti = await cancella(e.refs);
    if (falliti) nonEliminati.push(etichetta(e.lab, falliti));
  }
  for (const col of PER_ID) {
    try { const s = await getDoc(doc(db, col, project.id)); if (s.exists()) await deleteDoc(doc(db, col, project.id)); }
    catch { nonEliminati.push(col); }
  }
  // richieste commerciali e offerte restano, scollegate
  for (const [col, campo, campoOrg, campi2] of [
    ["richiesteCommerciali", "projectId", "orgId", { projectId: "", projectName: "", codiceCommessa: "" }],
    ["elaborazioniOfferte", "projectIdCollegato", "orgId", { projectIdCollegato: "", projectNomeCollegato: "" }],
  ]) {
    try {
      const s = await getDocs(query(collection(db, col), where(campo, "==", project.id), where(campoOrg, "==", org)));
      for (const d of s.docs) { try { await updateDoc(doc(db, col, d.id), campi2); } catch { /* resta collegata a una commessa che non c'è più */ } }
    } catch { /* raccolta non leggibile da questo utente */ }
  }

  // 4. traccia nel Log Modifiche e negli eventi
  const nome = project.name || "";
  await logModifica({ orgId: org, uid: autore?.uid || "", nomeUtente: autore?.nome || "", sezione: "commesse", refId: project.id, refNome: nome,
    campo: "eliminazione", campoLabel: "Commessa eliminata definitivamente", before: project.codiceCommessa ? `[${project.codiceCommessa}] ${nome}` : nome,
    after: nonEliminati.length ? `eliminata (non eliminati: ${nonEliminati.join(", ")})` : "eliminata con tutti i documenti collegati",
    projectId: project.id, projectName: nome });
  await logEvento({ orgId: org, tipo: "COMMESSA_ELIMINATA", categoria: "commesse", titolo: `Commessa eliminata: ${nome}`,
    descrizione: nonEliminati.length ? `Non eliminati: ${nonEliminati.join(", ")}` : "Eliminata con tutti i documenti collegati",
    uid: autore?.uid || "", nomeUtente: autore?.nome || "", projectId: project.id, projectName: nome, priorita: "alto" });

  // 5. conferma dal server: la commessa non deve esserci più
  let ancoraPresente = false;
  try { ancoraPresente = (await getDocFromServer(doc(db, "projects", project.id))).exists(); }
  catch { ancoraPresente = false; /* senza rete non si può confermare: l'eliminazione è comunque stata accettata */ }
  return { ok: !ancoraPresente, nonEliminati, ancoraPresente };
}
